import { Badge, CustomComponent, SimpleThumbnail, TruncateWithTooltip } from "@kaltura/mediaspace-shared-ui";
import { KalturaESearchEntryResult } from "kaltura-typescript-client/api/types/KalturaESearchEntryResult";
import { KalturaBaseEntry } from "kaltura-typescript-client/api/types/KalturaBaseEntry";
import {
    ScheduledBadgeWrap,
    StyledDescription,
    StyledExploreWrap,
    StyledInfo,
    StyledLink,
    StyledMediaCard,
    StyledName,
    StyledSearchResults,
    StyledSearchResultsWrap,
    StyledTags,
    StyledThumbWrap,
} from "./styled-components";
import {
    getDuration,
    getIsLive,
    HtmlReactParser,
    stripTags,
    translate,
    useMediaQuery,
} from "@kaltura/mediaspace-shared-utils";
import { Schedule } from "../schedule/Schedule";
import {
    ButtonClickAnalyticsType,
    KmsTypeComponent,
    KmsTypeSchedulingTimeInterval,
} from "@kaltura/mediaspace-shared-types";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import { Button } from "@kaltura/ds-react-components";
import { useButtonAnalytics, useEntryUrl } from "@kaltura/mediaspace-shared-hooks";

type CardMode = "list" | "search";

export interface MediaCardProps {
    /**
     * data to show, API search result
     */
    data: KalturaESearchEntryResult;

    /**
     * card mode.
     * <ul>
     *  <li>"list": non-textual search, filtering</li>
     *  <li>"search": textual search</li>
     * </ul>
     */
    mode: CardMode;

    /**
     * active mode. used in search lists. see `onMore` below.
     */
    active?: boolean;

    /**
     * callback for "more" / search results items click
     */
    onMore?: (searchResult: KalturaESearchEntryResult) => void;

    /**
     * Thumbnail image component to use instead of default thumbnail.
     */
    thumbnailComponent?: KmsTypeComponent;
}

const baseClassName = "kms-ds-item-media-card";

/**
 * non-textual-search result item
 */
export function MediaCard({ data, mode, active, thumbnailComponent, onMore }: MediaCardProps) {
    const sendButtonAnalytics = useButtonAnalytics();
    // cast entry type to allow "schedulingData" which doesn't exist on base-entry
    const entry = data.object as unknown as KalturaBaseEntry & { schedulingData: KmsTypeSchedulingTimeInterval };

    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
    const isNotTinyScreen = useMediaQuery(theme.breakpoints.up("sm"));

    const moreHandler = (fromResults: boolean) => {
        return () => {
            let eventName = "Media card - Explore";
            if (fromResults) {
                eventName = "Media card - Explore via search results";
            }

            sendButtonAnalytics(eventName, ButtonClickAnalyticsType.LAUNCH, entry.id);
            if (onMore) {
                onMore(data);
            }
        };
    };

    /**
     * send tag click analyitcs.
     * navigation is handled inside tags component.
     */
    const tagsAnalyticsHandler = () => {
        sendButtonAnalytics("Media card - Tags filter", ButtonClickAnalyticsType.SEARCH, entry.id);
    };

    const getNumberOfDescriptionLines = (isLargeScreen: boolean, hasScheduling: boolean) => {
        if (isLargeScreen) {
            return hasScheduling ? 1 : 2;
        }
        return hasScheduling ? 2 : 3;
    };

    const sendAnalytics = (isThumb: boolean, entryId: string) => {
        let name = "Media card - Name link";
        if (isThumb) {
            name = "Media card - Thumbnail link";
        }
        sendButtonAnalytics(name, ButtonClickAnalyticsType.LINK, entryId);
    };

    // use bigger sizes for search result - 'onMore' will be used to load the larger image size
    const width = mode === "search" ? 276 : 186;
    const height = mode === "search" ? 156 : 106;

    const entryUrl = useEntryUrl({ entry });

    return (
        <StyledMediaCard active={active} className={`${baseClassName}`}>
            <StyledThumbWrap className={`${baseClassName}-thumbnail`}>
                {thumbnailComponent ? (
                    <CustomComponent
                        component={thumbnailComponent}
                        entry={entry}
                        thumbnailUrl={`${entry.thumbnailUrl}/width/${width}/height/${height}/type/3`}
                        isLive={getIsLive(entry)}
                        entryUrl={entryUrl}
                        onClick={() => sendAnalytics(true, entry.id)}
                        className={`${baseClassName}-thumbnail-link`}
                        {...thumbnailComponent.props}
                    />
                ) : (
                    <StyledLink
                        href={entryUrl}
                        onClick={() => sendAnalytics(true, entry.id)}
                        className={`${baseClassName}-thumbnail-link`}
                    >
                        <SimpleThumbnail
                            thumbnailUrl={`${entry.thumbnailUrl}/width/${width}/height/${height}/type/3`}
                            name={entry.name}
                            duration={getDuration(entry)}
                            isLive={getIsLive(entry)}
                        />
                    </StyledLink>
                )}
            </StyledThumbWrap>
            <StyledInfo className={`${baseClassName}-info`}>
                <TruncateWithTooltip text={entry.name} lines={isNotTinyScreen ? 1 : 2}>
                    <StyledLink
                        href={entryUrl}
                        onClick={() => sendAnalytics(false, entry.id)}
                        className={`${baseClassName}-info-name`}
                        aria-label={entry.name}
                    >
                        <StyledName component="h3">{entry.name}</StyledName>
                    </StyledLink>
                </TruncateWithTooltip>

                {isNotTinyScreen && !!entry.schedulingData && (
                    <Schedule schedulingData={entry.schedulingData} className={`${baseClassName}-info-schedule`} />
                )}
                {!isNotTinyScreen && !!entry.schedulingData && (
                    <ScheduledBadgeWrap>
                        <Badge label={translate("scheduled")} size={"small"} mode={"light"} />
                    </ScheduledBadgeWrap>
                )}
                {(isLargeScreen || (isNotTinyScreen && mode === "list")) && (
                    <StyledDescription
                        lines={getNumberOfDescriptionLines(isLargeScreen, !!entry.schedulingData)}
                        showMore={false}
                        preventStateUpdate={true}
                        className={`${baseClassName}-info-description`}
                    >
                        {/* we use HtmlReactParser here to decode html entities */}
                        <>{HtmlReactParser(stripTags(entry.description))}</>
                    </StyledDescription>
                )}
                {mode === "list" && isLargeScreen && (
                    <StyledTags
                        tags={entry.tags}
                        clickable={true}
                        count={3}
                        countRest={true}
                        shortGap={!!entry.schedulingData}
                        additionalClickHandler={tagsAnalyticsHandler}
                        className={`${baseClassName}-info-tags`}
                    />
                )}
                {mode === "search" && isNotTinyScreen && (
                    <StyledSearchResultsWrap
                        shortGap={!!entry.schedulingData}
                        className={`${baseClassName}-info-search-results`}
                    >
                        <StyledSearchResults
                            className={"kms-ds-mediacard-search-results-summary"}
                            data={data}
                            onItemClick={moreHandler(true)}
                        />
                    </StyledSearchResultsWrap>
                )}
                {!isNotTinyScreen && (
                    <StyledExploreWrap shortGap={!!entry.schedulingData}>
                        {mode === "search" && (
                            <Button
                                color={"secondary"}
                                variant={"borderless"}
                                onClick={moreHandler(false)}
                                className={`${baseClassName}-explore-button`}
                            >
                                {translate("Explore")}
                            </Button>
                        )}
                    </StyledExploreWrap>
                )}
            </StyledInfo>
            {isNotTinyScreen && (
                <StyledExploreWrap shortGap={!!entry.schedulingData} className={`${baseClassName}-explore`}>
                    {mode === "search" && (
                        <Button
                            color={"secondary"}
                            variant={"borderless"}
                            onClick={moreHandler(false)}
                            className={`${baseClassName}-explore-button`}
                        >
                            {translate("Explore")}
                        </Button>
                    )}
                </StyledExploreWrap>
            )}
        </StyledMediaCard>
    );
}
